import { useState, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { CompanyLogo } from "@/components/CompanyLogo";
import { prettifySourceName } from "@/lib/sourceUtils";
import { getSentiment, getSimplifiedSentiment } from "@/utils/sentiment";

const PAGE_SIZE = 30;

// ─── Shared Constants ────────────────────────────────────────────────────────

const NEWS_CATEGORIES = [
  "Deals", "Valuations", "Leadership", "Governance", "Macroeconomic",
  "Earnings Results", "Regulatory / Policy", "Strategy Updates",
  "Operational Updates", "Competitive Dynamics", "Market / Industry Trends",
  "Capital Allocation", "Capital Structure / Financing", "Exit & Liquidity",
];

const RAW_TO_CURATED = {
  "general news": null,
  "investment or funding related news": "Capital Allocation",
  "investment or funding": "Capital Allocation",
  "investment": "Capital Allocation",
  "funding": "Capital Allocation",
  "venture capital": "Capital Allocation",
  "market update": "Market / Industry Trends",
  "market news": "Market / Industry Trends",
  "industry trends": "Market / Industry Trends",
  "earnings": "Earnings Results",
  "earnings report": "Earnings Results",
  "quarterly results": "Earnings Results",
  "financial results": "Earnings Results",
  "regulatory": "Regulatory / Policy",
  "policy": "Regulatory / Policy",
  "regulation": "Regulatory / Policy",
  "merger": "Deals",
  "acquisition": "Deals",
  "m&a": "Deals",
  "deal": "Deals",
  "ipo": "Exit & Liquidity",
  "exit": "Exit & Liquidity",
  "listing": "Exit & Liquidity",
  "leadership change": "Leadership",
  "management change": "Leadership",
  "ceo": "Leadership",
  "strategy": "Strategy Updates",
  "operational": "Operational Updates",
  "competitive": "Competitive Dynamics",
  "capital structure": "Capital Structure / Financing",
  "financing": "Capital Structure / Financing",
  "debt": "Capital Structure / Financing",
  "governance": "Governance",
  "valuation": "Valuations",
  "macroeconomic": "Macroeconomic",
  "macro": "Macroeconomic",
};

export function mapNewsCategory(primaryTag, headline) {
  const pt = (primaryTag || "").replace(/[\u00A0\s]+/g, " ").trim();
  const base = pt.split(",").map(t => t.trim().includes(" - ") ? t.trim().split(" - ")[0].trim() : t.trim()).filter(Boolean)[0] || "";
  if (base) {
    const exact = NEWS_CATEGORIES.find(c => c.toLowerCase() === base.toLowerCase());
    if (exact) return exact;
    const rawKey = base.toLowerCase().trim();
    if (rawKey in RAW_TO_CURATED && RAW_TO_CURATED[rawKey]) return RAW_TO_CURATED[rawKey];
    for (const [raw, curated] of Object.entries(RAW_TO_CURATED)) {
      if (curated && (rawKey.includes(raw) || raw.includes(rawKey))) return curated;
    }
  }

  // Keyword fallback from headline
  const h = ((headline || "") + " " + (primaryTag || "")).toLowerCase();
  if (/acqui|merger|m&a|takeover|buyout|bid/.test(h)) return "Deals";
  if (/ipo|listing|exit|spac|delist/.test(h)) return "Exit & Liquidity";
  if (/earning|revenue|profit|quarter|fiscal|financial result/.test(h)) return "Earnings Results";
  if (/regulat|policy|antitrust|complian|sanction|ban|blacklist|tariff/.test(h)) return "Regulatory / Policy";
  if (/valuat|worth|market cap/.test(h)) return "Valuations";
  if (/ceo|cfo|cto|appoint|resign|hire|fired|leadership/.test(h)) return "Leadership";
  if (/governance|board|shareholder|proxy/.test(h)) return "Governance";
  if (/invest|fund|raise|capital|venture|series [a-f]/.test(h)) return "Capital Allocation";
  if (/debt|bond|loan|credit|refinanc/.test(h)) return "Capital Structure / Financing";
  if (/strateg|pivot|restructur|reorgani|transform/.test(h)) return "Strategy Updates";
  if (/launch|product|feature|release|update|partner/.test(h)) return "Operational Updates";
  if (/compet|rival|market share|disrupt/.test(h)) return "Competitive Dynamics";
  if (/trend|industry|sector|market (shift|update|outlook)/.test(h)) return "Market / Industry Trends";
  if (/macro|inflation|gdp|interest rate|recession|econom/.test(h)) return "Macroeconomic";

  return "General";
}

// ─── Impact Level ────────────────────────────────────────────────────────────

export function getImpactLevel(story) {
  const impacts = story.portfolio_impact || [];
  const scales = impacts.map(p => (p.scale || "").toUpperCase()).filter(Boolean);
  if (scales.includes("CRITICAL") || scales.includes("HIGH")) return "HIGH";
  if (scales.includes("MEDIUM")) return "MEDIUM";
  if (scales.length > 0 && scales.every(s => s === "LOW")) return "LOW";
  if (scales.length > 0) return "MEDIUM";

  const sentiment = getSimplifiedSentiment(getSentiment(`${story.title || ""} ${story.snippet || ""}`));
  const sourceCount = story.source_count || 0;
  const companyCount = impacts.filter(p => p.company_name).length;

  if (sentiment === "NEGATIVE" && (sourceCount >= 5 || companyCount >= 3)) return "HIGH";
  if (sourceCount >= 3 || companyCount >= 2 || sentiment === "NEGATIVE") return "MEDIUM";
  return "LOW";
}

// ─── Attention Story Detection ───────────────────────────────────────────────

export function isAttentionStory(story) {
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
  const storyDate = new Date(story.first_seen_at || story.updated_at);
  if (isNaN(storyDate.getTime()) || storyDate < tenDaysAgo) return false;

  const hasCompany = (story.portfolio_impact || []).some(p => p.company_name);
  if (!hasCompany) return false;

  const sentiment = getSimplifiedSentiment(getSentiment(`${story.title || ""} ${story.snippet || ""}`));
  if (sentiment !== "NEGATIVE") return false;

  return getImpactLevel(story) === "HIGH";
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTimeAgo(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return ""; }
}

function getCompanies(story) {
  const impacts = story.portfolio_impact || [];
  const map = {};
  impacts.forEach(p => {
    if (p.company_name && !map[p.company_name]) {
      map[p.company_name] = {
        name: p.company_name,
        permalink: p.company_permalink || p.company_name.toLowerCase().replace(/\s+/g, "-"),
      };
    }
  });
  return Object.values(map);
}

function getSourcesList(story) {
  const articles = story.source_articles || story.articles || [];
  const rawNames = [...new Set(articles.map(a => (a.source_name || "").trim()).filter(Boolean))];
  if (!rawNames.length) {
    const count = story.source_count || 0;
    return { names: [], total: count };
  }
  return { names: rawNames.map(prettifySourceName), total: rawNames.length };
}

// ─── Style Maps ──────────────────────────────────────────────────────────────

const SENTIMENT_COLORS = {
  NEGATIVE: { bg: "#FEE2E2", color: "#DC2626" },
  POSITIVE: { bg: "#DCFCE7", color: "#16A34A" },
  NEUTRAL:  { bg: "#F3F4F6", color: "#6B7280" },
};

const IMPACT_COLORS = {
  HIGH:   { bg: "#FEE2E2", color: "#DC2626" },
  MEDIUM: { bg: "#FEF3C7", color: "#D97706" },
  LOW:    { bg: "#F3F4F6", color: "#6B7280" },
};

// ─── NewsTable Component ─────────────────────────────────────────────────────

export function NewsTable({ stories, onStoryClick, sortMode = "risk_priority", getStorySentiment, showAttention = false }) {
  const [page, setPage] = useState(0);
  const [expandedImpact, setExpandedImpact] = useState({});
  const [expandedSources, setExpandedSources] = useState({});

  const { attentionStories, displayStories } = useMemo(() => {
    if (!showAttention) {
      return { attentionStories: [], displayStories: stories };
    }
    const attention = stories
      .filter(isAttentionStory)
      .sort((a, b) => new Date(b.first_seen_at || b.updated_at) - new Date(a.first_seen_at || a.updated_at))
      .slice(0, 2);
    const attIds = new Set(attention.map(s => s.brief_id));
    const rest = stories.filter(s => !attIds.has(s.brief_id));
    return { attentionStories: attention, displayStories: [...attention, ...rest] };
  }, [stories, showAttention]);

  const attentionIds = useMemo(() => new Set(attentionStories.map(s => s.brief_id)), [attentionStories]);

  const totalPages = Math.ceil(displayStories.length / PAGE_SIZE);
  const rows = displayStories.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const showStart = displayStories.length > 0 ? page * PAGE_SIZE + 1 : 0;
  const showEnd = Math.min((page + 1) * PAGE_SIZE, displayStories.length);

  const resolveSentiment = (story) => {
    if (getStorySentiment) {
      const s = getStorySentiment(story);
      if (s && typeof s === "object" && !s.loading && s.value) return getSimplifiedSentiment(s.value);
      if (typeof s === "string") return s;
    }
    return getSimplifiedSentiment(getSentiment(`${story.title || ""} ${story.snippet || ""}`));
  };

  return (
    <div>
      {/* Showing counter */}
      <div className="flex items-center justify-end mb-3">
        <span className="text-[11px] text-[#9AA0A6]" data-testid="table-showing-count">
          Showing {showStart}-{showEnd} of {displayStories.length} stories
        </span>
      </div>

      {/* Attention Cards */}
      {attentionStories.length > 0 && page === 0 && (
        <div className="mb-5" data-testid="attention-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-[#DC2626]" />
            <span className="text-[11px] font-bold text-[#DC2626] uppercase tracking-wider">
              Requires Immediate Attention
            </span>
          </div>
          <div className="flex gap-3 mb-4">
            {attentionStories.map(story => {
              const companies = getCompanies(story);
              const co = companies[0];
              return (
                <div
                  key={story.brief_id}
                  className="flex-1 max-w-[320px] p-4 rounded-md cursor-pointer transition-shadow hover:shadow-md"
                  style={{ background: "#FFFBFB", border: "1px solid #FECACA", borderLeft: "4px solid #DC2626" }}
                  onClick={() => onStoryClick(story)}
                  data-testid={`attention-card-${story.brief_id}`}
                >
                  {co && (
                    <div className="flex items-center gap-2 mb-2">
                      <CompanyLogo name={co.name} permalink={co.permalink} size={20} />
                      <span className="text-sm font-bold text-[#1A1A1A]">{co.name}</span>
                    </div>
                  )}
                  <p className="text-[12px] text-[#4B5563] line-clamp-2 mb-2 leading-relaxed">
                    {story.snippet || story.title}
                  </p>
                  <span className="text-[10px] text-[#9AA0A6]">
                    {formatTimeAgo(story.first_seen_at || story.updated_at)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 8-Column Table */}
      <div className="bg-white rounded-md overflow-hidden" style={{ border: "1px solid #E5E7EB", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="w-full" style={{ minWidth: "960px" }} data-testid="news-table">
            <thead>
              <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                {[
                  ["Company", "13%"],
                  ["Headline", "21%"],
                  ["Category", "10%"],
                  ["Sentiment", "8%"],
                  ["Business Impact", "22%"],
                  ["Impact", "6%"],
                  ["Sources", "11%"],
                  ["Time", "6%"],
                ].map(([label, w]) => (
                  <th
                    key={label}
                    className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]"
                    style={{ width: w }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((story, idx) => {
                const isAttention = attentionIds.has(story.brief_id);
                const companies = getCompanies(story);
                const category = mapNewsCategory(story.primary_tag, story.title);
                const sentiment = resolveSentiment(story);
                const sStyle = SENTIMENT_COLORS[sentiment] || SENTIMENT_COLORS.NEUTRAL;
                const impactLevel = getImpactLevel(story);
                const iStyle = IMPACT_COLORS[impactLevel] || IMPACT_COLORS.LOW;

                const impacts = story.portfolio_impact || [];
                const firstImpactText = impacts[0]?.impact_description || impacts[0]?.value || "";
                const isExpImpact = expandedImpact[story.brief_id];

                const src = getSourcesList(story);
                const isExpSrc = expandedSources[story.brief_id];

                return (
                  <tr
                    key={story.brief_id}
                    className="cursor-pointer transition-colors"
                    style={{
                      borderBottom: "1px solid #F0F2F4",
                      borderLeft: isAttention ? "4px solid #DC2626" : "4px solid transparent",
                      background: idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA",
                    }}
                    onClick={() => onStoryClick(story)}
                    onMouseEnter={e => { e.currentTarget.style.background = "#F5F7FA"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA"; }}
                    data-testid={`table-row-${story.brief_id}`}
                  >
                    {/* Company */}
                    <td className="px-4 py-3 align-top">
                      {companies.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {companies.slice(0, 2).map(c => (
                            <div key={c.name} className="flex items-center gap-1.5">
                              <CompanyLogo name={c.name} permalink={c.permalink} size={20} />
                              <span className="text-[12px] font-semibold text-[#1A1A1A] truncate max-w-[100px]">{c.name}</span>
                            </div>
                          ))}
                          {isAttention && (
                            <span
                              className="text-[9px] font-bold text-[#DC2626] uppercase tracking-wide mt-0.5"
                              data-testid={`attention-badge-${story.brief_id}`}
                            >
                              Requires Immediate Attention
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#9AA0A6] italic">Industry</span>
                      )}
                    </td>

                    {/* Headline */}
                    <td className="px-4 py-3 align-top">
                      <p
                        className="text-[13px] font-semibold text-[#1A1A1A] line-clamp-2 leading-snug hover:text-[#2563EB] transition-colors"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {story.title}
                      </p>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3 align-top">
                      {category ? (
                        <span className="text-[11px] text-[#4B5563] leading-tight">{category}</span>
                      ) : (
                        <span className="text-[11px] text-[#D1D5DB]">-</span>
                      )}
                    </td>

                    {/* Sentiment */}
                    <td className="px-4 py-3 align-top">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                        style={{ background: sStyle.bg, color: sStyle.color }}
                        data-testid={`sentiment-${story.brief_id}`}
                      >
                        {sentiment}
                      </span>
                    </td>

                    {/* Business Impact */}
                    <td className="px-4 py-3 align-top" onClick={e => e.stopPropagation()}>
                      {firstImpactText ? (
                        <div>
                          <p className={`text-[12px] text-[#4B5563] leading-relaxed ${isExpImpact ? "" : "line-clamp-3"}`}>
                            {firstImpactText}
                          </p>
                          {firstImpactText.length > 100 && (
                            <button
                              onClick={() => setExpandedImpact(p => ({ ...p, [story.brief_id]: !p[story.brief_id] }))}
                              className="text-[10px] text-[#2563EB] hover:underline mt-1 block"
                              data-testid={`impact-toggle-${story.brief_id}`}
                            >
                              {isExpImpact ? "show less" : "view more"}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#D1D5DB]">-</span>
                      )}
                    </td>

                    {/* Impact Level */}
                    <td className="px-4 py-3 align-top">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                        style={{ background: iStyle.bg, color: iStyle.color }}
                        data-testid={`impact-level-${story.brief_id}`}
                      >
                        {impactLevel}
                      </span>
                    </td>

                    {/* Sources */}
                    <td className="px-4 py-3 align-top" onClick={e => e.stopPropagation()}>
                      {src.names.length > 0 ? (
                        <div className="text-[11px] text-[#6B7280]">
                          {isExpSrc ? (
                            <>
                              {src.names.map((name, i) => (
                                <div key={i}>{name}</div>
                              ))}
                              <button
                                onClick={() => setExpandedSources(p => ({ ...p, [story.brief_id]: false }))}
                                className="text-[10px] text-[#2563EB] hover:underline mt-0.5"
                              >
                                show less
                              </button>
                            </>
                          ) : (
                            <>
                              {src.names.slice(0, 2).map((name, i) => (
                                <div key={i}>{name}</div>
                              ))}
                              {src.total > 2 && (
                                <button
                                  onClick={() => setExpandedSources(p => ({ ...p, [story.brief_id]: true }))}
                                  className="text-[10px] text-[#2563EB] hover:underline mt-0.5"
                                >
                                  +{src.total - 2} more
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      ) : src.total > 0 ? (
                        <span className="text-[11px] text-[#9AA0A6]">{src.total}</span>
                      ) : (
                        <span className="text-[11px] text-[#D1D5DB]">-</span>
                      )}
                    </td>

                    {/* Time */}
                    <td className="px-4 py-3 align-top">
                      <span className="text-[11px] text-[#9AA0A6] whitespace-nowrap">
                        {formatTimeAgo(story.first_seen_at || story.updated_at)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid #F0F2F4" }}>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-40 transition-colors hover:bg-[#F4F5F6]"
              style={{ border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#3A3A3A" }}
              data-testid="table-prev-page"
            >
              Previous
            </button>
            <span className="text-[12px] text-[#9AA0A6]">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-40 transition-colors hover:bg-[#F4F5F6]"
              style={{ border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#3A3A3A" }}
              data-testid="table-next-page"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
